import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import ChatWebSocketService from '../services/ChatWebSocketService';
import SessionService from '../services/SessionService';

interface SyncProgress {
    current: number;
    total: number;
    message?: string;
    percentage: number;
}

interface SyncState {
    isActive: boolean;
    sectorId: number | null;
    stage: 'idle' | 'contacts' | 'groups' | 'completed' | 'error';
    contactProgress: SyncProgress;
    groupProgress: SyncProgress;
    errorMessage?: string;
}

interface SyncContextType {
    syncState: SyncState;
    startSync: (sectorId: number, kind?: 'contacts' | 'groups') => void;
    resetSync: () => void;
    isSyncing: boolean;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

const STORAGE_KEY = '@app/sync_state';

const emptyProgress = (): SyncProgress => ({ current: 0, total: 0, percentage: 0 });

const idleState = (): SyncState => ({
    isActive: false,
    sectorId: null,
    stage: 'idle',
    contactProgress: emptyProgress(),
    groupProgress: emptyProgress(),
});

const toProgress = (raw: any): SyncProgress => ({
    current: Number(raw?.current) || 0,
    total: Number(raw?.total) || 0,
    percentage: Number(raw?.percentage) || 0,
    ...(typeof raw?.message === 'string' ? { message: raw.message } : {}),
});

/**
 * Estado salvo por versões antigas pode não ter `groupProgress` (ou outro campo); a tela de Conexões lê
 * `.total` dos dois progressos em todo card e quebrava inteira. Restaura só o que tem formato válido.
 */
export const restoreSyncState = (saved: string | null): SyncState => {
    if (!saved) return idleState();
    try {
        const parsed = JSON.parse(saved);
        if (!parsed || typeof parsed !== 'object') return idleState();
        if (parsed.stage !== 'contacts' && parsed.stage !== 'groups') return idleState();
        const sectorId = Number(parsed.sectorId);
        if (!parsed.isActive || !sectorId) return idleState();
        return {
            isActive: true,
            sectorId,
            stage: parsed.stage,
            contactProgress: toProgress(parsed.contactProgress),
            groupProgress: toProgress(parsed.groupProgress),
        };
    } catch (e) {
        console.error('Erro ao restaurar sync state:', e);
        return idleState();
    }
};

export const SyncProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [syncState, setSyncState] = useState<SyncState>(() => restoreSyncState(localStorage.getItem(STORAGE_KEY)));

    useEffect(() => {
        if (syncState.isActive) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(syncState));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, [syncState]);

    useEffect(() => {
        const handleSyncProgress = (event: any, forcedType?: string) => {
            const payload = event?.payload ?? event ?? {};
            const sectorId = Number(payload.sectorId ?? event?.sectorId ?? 0);
            if (!sectorId) return;

            const status = String(payload.status ?? '').toLowerCase();
            const current = Number(payload.currentCount ?? payload.processed_contacts ?? 0) || 0;
            const total = Number(payload.totalCount ?? payload.total_contacts ?? 0) || 0;
            const percentage = Number(payload.percentage) || (total > 0 ? Math.round((current * 100) / total) : 0);
            const message = payload.message || payload.contactName || '';

            const rawType = forcedType || event?.type || '';
            const isGroup =
                rawType === 'group_sync_status' ||
                (payload.syncType === 'groups') ||
                (typeof message === 'string' && /group/i.test(message) && rawType !== 'contact_sync_status');

            setSyncState(prev => {
                if (prev.isActive && prev.sectorId != null && prev.sectorId !== sectorId) return prev;

                const next: SyncState = {
                    ...prev,
                    isActive: true,
                    sectorId,
                    errorMessage: undefined,
                };

                if (isGroup) {
                    next.stage = 'groups';
                    next.groupProgress = { current, total, percentage, message };
                } else {
                    next.stage = 'contacts';
                    next.contactProgress = { current, total, percentage, message };
                }

                if (status === 'completed') {
                    next.stage = 'completed';
                    next.isActive = false;
                    if (isGroup) next.groupProgress = { ...next.groupProgress, percentage: 100 };
                    else next.contactProgress = { ...next.contactProgress, percentage: 100 };
                } else if (status === 'error' || status === 'failed') {
                    next.stage = 'error';
                    next.isActive = false;
                    next.errorMessage = message || 'Synchronization failed';
                }

                return next;
            });
        };

        const onSyncProgress = (data: any) => handleSyncProgress(data, 'sync_progress');
        const onGroupSync = (data: any) => handleSyncProgress(data, 'group_sync_status');
        const onContactSync = (data: any) => handleSyncProgress(data, 'contact_sync_status');

        const unsub1 = ChatWebSocketService.on('sync_progress', onSyncProgress);
        const unsub2 = ChatWebSocketService.on('group_sync_status', onGroupSync);
        const unsub3 = ChatWebSocketService.on('contact_sync_status', onContactSync);

        return () => {
            unsub1();
            unsub2();
            unsub3();
        };
    }, []);

    const startSync = (sectorId: number, kind: 'contacts' | 'groups' = 'contacts') => {
        // Progresso chega via WS do setor — só reconecta se ainda não estamos nessa room.
        const token = SessionService.getToken();
        if (token && !ChatWebSocketService.isConnectedToSector(sectorId)) {
            void ChatWebSocketService.connect(token, sectorId).catch((err) => {
                console.warn('[Sync] WebSocket connect for progress failed:', err);
            });
        }

        setSyncState({
            isActive: true,
            sectorId,
            stage: kind,
            contactProgress: kind === 'contacts'
                ? { current: 0, total: 0, percentage: 0, message: 'Starting contacts sync...' }
                : emptyProgress(),
            groupProgress: kind === 'groups'
                ? { current: 0, total: 0, percentage: 0, message: 'Starting groups sync...' }
                : emptyProgress(),
        });
    };

    const resetSync = () => setSyncState(idleState());

    return (
        <SyncContext.Provider value={{ syncState, startSync, resetSync, isSyncing: syncState.isActive }}>
            {children}
        </SyncContext.Provider>
    );
};

export const useSync = () => {
    const context = useContext(SyncContext);
    if (context === undefined) {
        throw new Error('useSync must be used within a SyncProvider');
    }
    return context;
};
